import React, { useState, useRef, useEffect } from "react";
import { Mic, MicOff, Check, X, Square } from "lucide-react";
import { transcribeAudio } from "../../service/apiService";
import { toast } from "sonner";
import Tooltip from "../Tooltip/Tooltip";

interface DictateComponentProps {
  onTranscription: (text: string) => void;
  onCancel?: () => void;
  className?: string;
  disabled?: boolean;
}

const DictateComponent: React.FC<DictateComponentProps> = ({
  onTranscription,
  onCancel,
  className = "",
  disabled = false,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/wav" });

        // Stop all tracks to free up the microphone
        stream.getTracks().forEach(track => track.stop());

        // Immediately start transcription
        await transcribeAudioBlob(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert("Error accessing microphone. Please check your permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    setIsProcessing(true); // Show processing state immediately

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const transcribeAudioBlob = async (audioBlob: Blob) => {
    try {
      const result = await transcribeAudio(audioBlob);

      if (result.text) {
        onTranscription(result.text);
        toast.success("Audio transcribed successfully!");
      } else {
        throw new Error("No transcription received");
      }

      // Reset state
      resetState();

    } catch (error) {
      console.error("Error transcribing audio:", error);
      toast.error("Error transcribing audio. Please try again.");
      resetState();
    }
  };

  const resetState = () => {
    setIsRecording(false);
    setIsProcessing(false);
    setRecordingTime(0);
    audioChunksRef.current = [];

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isRecording) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <div className="flex items-center space-x-2 bg-red-50 border border-red-200 rounded-full px-2 sm:px-3 py-1 min-w-0">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse flex-shrink-0"></div>
          <span className="text-red-700 text-sm font-medium whitespace-nowrap hidden sm:inline">
            Recording {formatTime(recordingTime)}
          </span>
          {/* Mobile: show only timer */}
          <span className="text-red-700 text-xs font-medium whitespace-nowrap sm:hidden">
            {formatTime(recordingTime)}
          </span>
        </div>
        <Tooltip label="Stop recording" side="top">
          <button
            type="button"
            onClick={stopRecording}
            className="bg-red-500 hover:bg-red-600 text-white rounded-full p-2 transition-colors flex-shrink-0"
          >
            <Square size={16} />
          </button>
        </Tooltip>
      </div>
    );
  }

  if (isProcessing) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <div className="flex items-center space-x-2 bg-blue-50 border border-blue-200 rounded-full px-2 sm:px-3 py-1">
          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-blue-700 text-sm font-medium whitespace-nowrap hidden sm:inline">
            Transcribing...
          </span>
          <span className="text-blue-700 text-xs font-medium whitespace-nowrap sm:hidden">
            Processing
          </span>
        </div>
      </div>
    );
  }

  return (
    <Tooltip label="Start dictation" side="top">
      <button
        type="button"
        onClick={startRecording}
        disabled={disabled}
        className={`bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary disabled:bg-surface-2 disabled:text-subtle disabled:cursor-not-allowed rounded-full p-2 inline-flex text-sm font-medium h-8 transition-all duration-200 ${className}`}
        style={{ display: 'none' }}
      >
        <Mic size={17} />
      </button>
    </Tooltip>
  );
};

export default DictateComponent;